<template>
  <!-- MOBILE (my-bets page only): card layout matching the active-wagers
       cards. The modals that reuse this component keep the classic row on all
       screen sizes. All colors come from theme tokens so the card renders
       correctly in both light and night themes. -->
  <div
    v-if="!modal"
    class="offer-card d-md-none"
    role="button"
    tabindex="0"
    @click="editOrder"
    @keydown.enter="editOrder"
  >
    <div class="offer-card__header">
      <template v-if="!showEventName && session.game.participants.length >= 2">
        <div class="offer-card__matchup">
          <team-logo
            :participant="session.game.participants[0]"
            :sport="session.game.sport"
            :size="16"
            validation-mode="show"
          />
          <span class="offer-card__team-name">{{ homeName }}</span>
          <span class="offer-card__at">@</span>
          <team-logo
            :participant="session.game.participants[1]"
            :sport="session.game.sport"
            :size="16"
            validation-mode="show"
          />
          <span class="offer-card__team-name">{{ awayName }}</span>
        </div>
      </template>
      <template v-else>
        <span class="offer-card__event">{{ displayEventName }}</span>
      </template>
      <span class="offer-card__time">{{ dayLabel }} · {{ timeLabel }}</span>
    </div>

    <div class="offer-card__pick-row">
      <span v-if="sideIcon.kind !== 'none'" class="offer-card__side-icon">
        <team-logo
          v-if="sideIcon.kind === 'logo'"
          :participant="sideIcon.participant"
          :sport="session.game.sport"
          :size="20"
          validation-mode="show"
        />
        <font-awesome-icon
          v-else-if="sideIcon.kind === 'arrow'"
          :icon="sideIcon.dir === 'up' ? faArrowUp : faArrowDown"
          :class="
            sideIcon.dir === 'up'
              ? 'side-arrow side-arrow--up'
              : 'side-arrow side-arrow--down'
          "
        />
        <span v-else-if="sideIcon.kind === 'flag'" class="side-flag">
          <font-awesome-icon :icon="faFlag" />
        </span>
      </span>
      <span class="offer-card__pick">{{ pickString }}</span>
      <span class="offer-card__badge">{{ fillBadge }}</span>
    </div>

    <div class="offer-card__amounts">
      <div class="offer-card__amount">
        <span class="offer-card__amount-label">{{ riskLabel }}</span>
        <span class="offer-card__amount-value"
          >${{ sessionBetsRoundFormatted(orderRisk) }}</span
        >
      </div>
      <div class="offer-card__amount offer-card__amount--win">
        <span class="offer-card__amount-label">Win</span>
        <span class="offer-card__amount-value"
          >${{ sessionBetsRoundFormatted(orderWin) }}</span
        >
      </div>
    </div>

    <div class="offer-card__footer">
      <span class="offer-card__expiry">Expires {{ displayExpiry }}</span>
      <div class="offer-card__actions">
        <button
          class="offer-card__btn offer-card__btn--edit"
          @click.stop="editOrder"
        >
          <font-awesome-icon :icon="faEdit" />
          Edit
        </button>
        <button
          class="offer-card__btn offer-card__btn--cancel"
          :class="{ 'offer-card__btn--cancel-armed': cancelArmed }"
          @click.stop="onCancelTap"
        >
          {{ cancelArmed ? 'Confirm?' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>

  <!-- CLASSIC ROW: desktop always; mobile only inside modals. -->
  <div
    :class="{
      betWin: session.outcome === 'winner',
      betLoss: session.outcome === 'loser',
      betDraw: session.outcome === 'refunded',
      betNormal:
        !session.graded || (session.cancelled && session.takenRatio === 0),
      'px-0': modal,
      'd-none d-md-flex': !modal,
    }"
    class="row col-12 bet py-3 mx-0 mb-1"
  >
    <div class="col-12">
      <div class="row justify-content-start justify-content-md-center">
        <div
          v-if="!modal"
          class="d-none d-md-flex col-2 justify-content-center flex-nowrap align-items-center"
        >
          <div class="bet-time">
            <!--  <font-awesome-icon
                :icon="['fas', 'clock']"
              />️ !-->
            {{ dayjs(session.game.start).format('hh:mm A DD MMM') }}
          </div>
        </div>
        <div
          class="col-10 d-flex d-md-block justify-content-center flex-nowrap align-items-center"
          :class="{
            'col-md-7 text-center': modal,
            'col-md-6 col-lg-5': !modal,
          }"
        >
          <span
            v-if="type === 'open-offers'"
            role="button"
            tabindex="0"
            class="bet-edit-btn mr-1"
            @click="editOrder"
          >
            <font-awesome-icon :icon="faEdit" />️
          </span>
          <span v-if="type !== 'active-wagers'" class="bet-main-info">
            <span
              v-if="modal && fromPaused"
              class="resume-offer-btn"
              @click.stop="resumeOffer"
            >
              Submit
            </span>

            {{
              session.game.league && !session.game.league.includes('PROPS')
                ? getSideString(session.game, session)
                : getSideStringProps(props.session.game, session)
            }}
            {{ formatOddsNoCommission(session.odds) }}
          </span>
          <informational-tooltip
            v-if="
              session.game.participants[0]?.mainPitcher &&
              (session.game.league === 'PGA' ||
                (session.game.league?.toLowerCase() || '').includes('series') ||
                (session.game.league?.toLowerCase() || '').includes('custom'))
            "
            class="text-lowercase font-weight-light"
            :text-content="`${session.game.participants[0].mainPitcher}`"
          />
          <div class="bet-teams font-weight-normal">
            {{
              userStore.user.defaultRotationNumbers ||
              userStore.user.displayRotationNumbers
                ? session.game.participants[0].rotationNumber
                : ''
            }}
            {{ session.game.participants[0].longName }} @
            {{
              userStore.user.defaultRotationNumbers ||
              userStore.user.displayRotationNumbers
                ? session.game.participants[1].rotationNumber
                : ''
            }}
            {{ session.game.participants[1].longName }}
            <span v-if="modal" class="bet-teams">
              - {{ dayjs(session.game.start).format('hh:mm A DD MMM') }}
            </span>
          </div>
        </div>
        <div
          class="col-2 d-md-none d-flex justify-content-center flex-nowrap align-items-center bet-risk"
        >
          {{ formatMobileRiskWin(unmatchedPortion) }}/{{
            formatMobileRiskWin(calculateWin(unmatchedPortion, session.odds))
          }}
        </div>
        <div
          class="col-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk"
        >
          ${{ sessionBetsRound(orderRisk) }}
        </div>
        <div
          class="col-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk"
        >
          ${{ sessionBetsRound(orderWin) }}
        </div>
        <div
          class="d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-time text-center"
          :class="{ 'col-3': modal, 'col-2': !modal }"
        >
          {{ displayExpiry }}
        </div>
        <div
          v-if="!modal"
          class="col-1 d-lg-flex d-none justify-content-center flex-nowrap align-items-center bet-plus"
          @click="displayBet"
        >
          <font-awesome-icon :icon="faPlus" />
        </div>
        <div
          class="col-md-none col-10 d-md-none d-flex justify-content-center flex-nowrap align-items-center bet-small"
        >
          {{
            userStore.user.defaultRotationNumbers ||
            userStore.user.displayRotationNumbers
              ? session.game.participants[0].rotationNumber
              : ''
          }}
          {{ session.game.participants[0].longName }} @
          {{
            userStore.user.defaultRotationNumbers ||
            userStore.user.displayRotationNumbers
              ? session.game.participants[1].rotationNumber
              : ''
          }}
          {{ session.game.participants[1].longName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
//CORE
import {
  calculateWin,
  getSideString,
  noDecimal,
  getSideStringProps,
  getBetSideIcon,
  getTeamDisplayName,
  stripPropFlags,
} from '@/core/utils';
import { formatOddsNoCommission } from '@/core/oddsFormatUtils';
import { computed, ref, onBeforeUnmount } from 'vue';
import { useUserStore } from '@/stores/user';

//COMPONENTS
import dayjs from 'dayjs';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import TeamLogo from '@/components/global/TeamLogo.vue';

//ICONS
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { faFlag } from '@fortawesome/free-solid-svg-icons/faFlag';

//PROPS
const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  modal: {
    type: Boolean,
    required: false,
    default: false,
  },
  fromPaused: {
    type: Boolean,
    required: false,
    default: false,
  },
});

//CONST
const userStore = useUserStore();

//COMPUTED
const orderRisk = computed(() => {
  return props.session.bet * (1 - props.session.takenRatio);
});

const orderWin = computed(() => {
  return calculateWin(orderRisk.value, props.session.odds);
});

const unmatchedPortion = computed(() => {
  return props.session.bet * (1 - props.session.takenRatio);
});

const displayExpiry = computed(() => {
  const session = props.session || {};
  const useGameStart = session.gameStartExpiry === true || !session.expiry;
  const sourceDate =
    (useGameStart ? session.game?.start : session.expiry) ?? null;
  const parsed = dayjs(sourceDate);
  return parsed.isValid() ? parsed.format('hh:mm A DD MMM') : '--';
});

//COMPUTED (mobile card)
const showEventName = computed(() => {
  const game = props.session.game || {};
  return Boolean(
    game.isSpecials ||
      game.isFutures ||
      (game.league && game.league.includes('PROPS')),
  );
});

const displayEventName = computed(
  () => stripPropFlags(props.session.game?.eventName) || '',
);

// Mobile card header uses the shortest available team label to keep the
// matchup compact, mirroring the active-wagers card.
function shortTeamName(participant) {
  if (!participant) return '';
  return participant.shortName || getTeamDisplayName(participant);
}
const homeName = computed(() =>
  shortTeamName(props.session.game?.participants?.[0]),
);
const awayName = computed(() =>
  shortTeamName(props.session.game?.participants?.[1]),
);

const timeLabel = computed(() =>
  dayjs(props.session.game?.start).format('h:mm A'),
);

const dayLabel = computed(() => {
  const start = dayjs(props.session.game?.start).startOf('day');
  const diff = start.diff(dayjs().startOf('day'), 'day');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return dayjs(props.session.game?.start).format('ddd D MMM');
});

const sideIcon = computed(() =>
  getBetSideIcon(props.session, props.session.game),
);

const pickString = computed(() => {
  const game = props.session.game;
  const side =
    game.league && !game.league.includes('PROPS')
      ? getSideString(game, props.session)
      : getSideStringProps(game, props.session);
  return `${side} ${formatOddsNoCommission(props.session.odds)}`.trim();
});

// Makes the unfilled status unmistakable; surfaces partial fills too.
const fillBadge = computed(() => {
  const ratio = props.session.takenRatio || 0;
  if (ratio > 0) return `${Math.round(ratio * 100)}% Filled`;
  return 'Unfilled';
});

const riskLabel = computed(() => {
  const ratio = props.session.takenRatio || 0;
  return ratio > 0 ? 'Unmatched Risk' : 'Risk';
});

//FUNCTIONS
function formatMobileRiskWin(value) {
  return noDecimal(value || 0).toLocaleString('en-US');
}

function sessionBetsRoundFormatted(RiskOrWin) {
  const parts = String(sessionBetsRound(RiskOrWin)).split('.');
  parts[0] = Number(parts[0]).toLocaleString('en-US');
  return parts.join('.');
}

// Cancel is destructive and easy to fat-finger on mobile, so the button arms
// on first tap and only cancels on a confirming second tap.
const cancelArmed = ref(false);
let cancelDisarmTimer = null;

function onCancelTap() {
  if (!cancelArmed.value) {
    cancelArmed.value = true;
    cancelDisarmTimer = setTimeout(() => {
      cancelArmed.value = false;
    }, 3000);
    return;
  }
  clearTimeout(cancelDisarmTimer);
  cancelArmed.value = false;
  // The my-bets page handler expects a session ID (the modals, which pass the
  // whole session, use the classic row and never render this card).
  emit('cancelOffer', props.session.id);
}

onBeforeUnmount(() => clearTimeout(cancelDisarmTimer));

// Desktop pending-bets risk/win formatter:
//   - $1000+ rounds to whole dollars (no cents needed at that size)
//   - whole-dollar amounts render without trailing ".00"  → 145
//   - anything with cents renders to 2 decimals             → 145.50
function sessionBetsRound(RiskOrWin) {
  const n = Number.parseFloat(RiskOrWin);
  if (!Number.isFinite(n)) return RiskOrWin;
  if (n >= 1000) return String(Math.round(n));
  const fixed = n.toFixed(2);
  return fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed;
}

//EMITTERS
const emit = defineEmits([
  'displayBet',
  'editOrder',
  'cancelOffer',
  'resumeOffer',
]);

function displayBet() {
  emit('displayBet', props.session);
}

function editOrder() {
  emit('editOrder', props.session);
}

function resumeOffer() {
  emit('resumeOffer', props.session.id);
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.bet-edit-btn {
  cursor: pointer;
  color: main.$color-primary_06;

  &:hover {
    color: main.$green-500;
  }

  @media screen and (min-width: map.get(main.$grid-breakpoints, md)) {
    line-height: 1rem;
  }
}

.betWin {
  background: main.$green-402;
}

.betLoss {
  background: main.$red-101;
}

.betDraw {
  background: main.$yellow-090;
}

.betNormal {
  background: main.$grey-500;
}

.bet {
  border: 3px;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  color: main.$blue-1100;
  font-size: small;
  margin: 0;
  position: relative;
  width: 100%;

  &-main-info {
    text-transform: uppercase;
    font-weight: main.$font-weight-semibold;
    color: main.$color-primary_06;
    font-size: main.$font-size-sm;
  }

  &-time {
    font-size: x-small;
    text-transform: uppercase;
    color: main.$black-203;
    font-weight: main.$font-weight-normal;
  }

  &-teams {
    font-size: x-small;
    text-transform: uppercase;
    color: main.$black-203;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      display: none;
    }
  }

  &-risk {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }

  &-plus {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    cursor: pointer;
    font-weight: main.$font-weight-bold;
  }

  &-small {
    color: main.$black-203;
    font-size: x-small;
    text-transform: uppercase;
  }

  &-participants {
    font-weight: main.$font-weight-semibold;
  }

  &-section-title {
    text-transform: uppercase;
    font-size: main.$font-size-sm;
    color: main.$color-primary;
    font-weight: main.$font-weight-semibold;
  }

  &-section-value {
    font-weight: main.$font-weight-semibold;
  }
}

.cancel-button {
  width: 100%;
  padding: 0.35rem 1.1rem;
  background: main.$red-301;
}

.win,
.lose,
.cancelled,
.refunded {
  margin: 0;
  padding: 0.35rem 2rem;
  text-align: center;
  display: inline-block;
  font-family: inherit;
  border-radius: 4px;
  font-size: main.$font-size-xs;
  color: main.$white;
}

.win {
  background: main.$green-302;
}

.lose {
  background: main.$red-500;
}

.cancelled,
.refunded {
  background: main.$grey-1701;
}

.currently-risking {
  font-size: main.$font-size-sm;
}

.delete-offer-btn {
  color: main.$red-500;
  cursor: pointer;
  line-height: 0px;

  &:hover {
    color: main.$red-101;
  }
}

.resume-offer-btn {
  color: main.$green-700;
  cursor: pointer;
  line-height: 0px;
  padding-right: 5px;

  &:hover {
    color: main.$green-203;
  }
}

/* Mobile open-offer card. Mirrors the active-wagers market-card so the two
   my-bets tabs feel like one system, with offer-specific extras: an unfilled
   badge, the expiry, and edit/cancel actions. */
.offer-card {
  background: main.$white_01;
  border: main.$border-02;
  border-radius: 12px;
  box-shadow: main.$box-shadow-02;
  padding: 0.875rem 1rem;
  margin-bottom: 0.625rem;
  cursor: pointer;
  width: 100%;

  &:focus-visible {
    outline: 2px solid main.$blue-901;
    outline-offset: -2px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid main.$blue-901_01;
  }

  &__matchup {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-width: 0;
    overflow: hidden;

    :deep(.team-logo) {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      opacity: 0.85;

      img {
        display: block;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
    }
  }

  &__team-name {
    font-size: main.$font-size-xs;
    font-weight: main.$font-weight-normal;
    color: main.$grey-2400;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  &__at {
    color: main.$grey-2400;
    opacity: 0.6;
    font-size: main.$font-size-xs;
    margin: 0 0.05rem;
  }

  &__event {
    font-size: main.$font-size-xs;
    font-weight: main.$font-weight-normal;
    color: main.$grey-2400;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__time {
    font-size: main.$font-size-xs;
    color: main.$grey-2400;
    white-space: nowrap;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__pick-row {
    display: flex;
    align-items: center;
    margin-top: 0.7rem;
  }

  &__side-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
    margin-right: 0.45rem;

    :deep(.team-logo) {
      width: 24px;
      height: 24px;

      img {
        display: block;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
    }
  }

  &__pick {
    font-size: 1.0625rem;
    font-weight: main.$font-weight-bold;
    color: main.$blue-1100;
    text-transform: uppercase;
    line-height: 1.2;
  }

  /* The "this is not a live bet yet" signal: a quiet outlined chip pushed to
     the right of the pick. */
  &__badge {
    margin-left: auto;
    flex-shrink: 0;
    font-size: main.$font-size-xs;
    font-weight: main.$font-weight-semibold;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: main.$grey-2400;
    border: 1px solid main.$blue-901_01;
    border-radius: 999px;
    padding: 0.1rem 0.5rem;
  }

  &__amounts {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.7rem;
    padding-top: 0.6rem;
    border-top: 1px solid main.$blue-901_01;
  }

  &__amount {
    display: flex;
    flex-direction: column;
  }

  &__amount--win {
    text-align: right;
  }

  &__amount-label {
    font-size: main.$font-size-xs;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: main.$grey-2400;
    margin-bottom: 0.1rem;
  }

  &__amount-value {
    font-size: 1rem;
    font-weight: main.$font-weight-bold;
    color: main.$blue-1100;
    font-variant-numeric: tabular-nums;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.7rem;
    padding-top: 0.6rem;
    border-top: 1px solid main.$blue-901_01;
  }

  &__expiry {
    font-size: main.$font-size-xs;
    color: main.$grey-2400;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  &__btn {
    font-size: main.$font-size-xs;
    font-weight: main.$font-weight-semibold;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-radius: 6px;
    padding: 0.3rem 0.75rem;
    background: transparent;
    line-height: 1;
  }

  &__btn--edit {
    color: main.$blue-1100;
    border: 1px solid main.$blue-901_01;
  }

  &__btn--cancel {
    color: main.$red-500;
    border: 1px solid main.$red-500;
  }

  &__btn--cancel-armed {
    background: main.$red-500;
    color: main.$white;
  }

  .side-arrow {
    font-size: 0.9em;
    line-height: 1;
    color: main.$grey-2400;
    margin-right: 0.4rem;
  }

  .side-flag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #868c9e;
    color: #fff;
    font-size: 0.55em;
    line-height: 1;
    margin-right: 0.4rem;
  }
}
</style>
